import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Clock, ExternalLink } from "lucide-react";

export default function Vicenza() {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
  }, []);

  if (!info) return <div className="py-32 text-center text-[#5C5C59]">…</div>;

  const lang = i18n.language;
  const fallback = { at: "de", ar: "en" }[lang];
  const descKey = `desc_${lang}`;
  const fbKey = fallback ? `desc_${fallback}` : null;

  return (
    <div data-testid="vicenza-page" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
            {t("vicenza.eyebrow")}
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none mt-5">
            {t("vicenza.title")}
          </h1>
          <p className="text-[#5C5C59] mt-6 leading-relaxed">{t("vicenza.subtitle")}</p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {info.attractions.map((a, idx) => {
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.name + ', Vicenza')}`;
            return (
              <article
                key={idx}
                data-testid={`vicenza-card-${idx}`}
                className="group bg-[#F3F1EC] border border-[#E5E3D8] overflow-hidden flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#E5E3D8]">
                  {a.image && (
                    <img
                      src={a.image}
                      alt={a.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#8B3A3A] font-semibold">
                      {a.type}
                    </span>
                    {a.distance_min && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#5C5C59]">
                        <Clock className="w-3 h-3" strokeWidth={1.5} />
                        {a.distance_min} {t("vicenza.minutesAway")}
                      </span>
                    )}
                  </div>
                  <h2 className="font-serif text-2xl tracking-tight mt-3 leading-tight">
                    {a.name}
                  </h2>
                  <p className="text-sm text-[#5C5C59] mt-3 leading-relaxed flex-1">
                    {a[descKey] || (fbKey && a[fbKey]) || a.desc_it}
                  </p>
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`vicenza-link-${idx}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm tracking-wide text-[#8B3A3A] link-underline self-start"
                  >
                    {t("vicenza.readMore")} <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
